import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  createDepreciationRun,
  fetchDepreciationRuns,
  fetchFinancialPeriods,
  postDepreciationRun,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const DepreciationPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ period_id: "", run_date: todayIsoDate(), description: "Monthly depreciation" });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  useEffect(() => {
    if (!branchId) return;
    fetchFinancialPeriods(branchId).then((p) => {
      setPeriods(p || []);
      const open = (p || []).find((x) => x.status === "open");
      if (open) setForm((prev) => ({ ...prev, period_id: open.id }));
    });
  }, [branchId]);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchDepreciationRuns(branchId).then(setRows), { errorMessage: "Failed to load depreciation runs." })
      .catch((err) => setError(formatApiError(err, "Failed to load depreciation runs.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const loadRef = useLatestRef(load);

  const postRun = useCallback(
    (row) => {
      confirm({
        title: "Post depreciation run?",
        message: "This will update asset accumulated depreciation and post a journal entry.",
        confirmLabel: "Post",
        successMessage: "Depreciation posted.",
        onConfirm: async () => {
          await postDepreciationRun(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Date", cell: (r) => formatDate(r.run_date), width: "110px" },
      { name: "Period", selector: (r) => r.period_name, width: "120px" },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      { name: "Total", cell: (r) => formatTZS(r.total_depreciation), right: true, width: "130px" },
      {
        name: "Status",
        cell: (r) => <Badge color={r.status === "posted" ? "success" : "warning"}>{r.status}</Badge>,
        width: "90px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: "eye",
                label: "View run",
                onClick: () => setDetail(r),
              },
              r.status === "draft" && {
                icon: "check",
                label: "Post depreciation",
                onClick: () => postRun(r),
                variant: "primary",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [postRun]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createDepreciationRun({
            branch_id: branchId,
            period_id: form.period_id,
            run_date: form.run_date,
            description: form.description,
          }),
        { successMessage: "Depreciation run created." }
      );
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const lineColumns = [
    { name: "Asset", selector: (r) => `${r.asset_tag} — ${r.asset_name}`, grow: 2 },
    { name: "Amount", cell: (r) => formatTZS(r.amount), right: true, width: "130px" },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Depreciation" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Depreciation runs</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)} disabled={!form.period_id}>Run depreciation</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        {detail && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Run detail — {formatTZS(detail.total_depreciation)}</h6>
              <Button size="sm" color="light" className="border" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div className="card-body p-0">
              <DataTable columns={lineColumns} data={detail.lines || []} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} dense />
            </div>
          </div>
        )}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>Create depreciation run</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Period</Label>
                <Input type="select" required value={form.period_id} onChange={(e) => setForm({ ...form, period_id: e.target.value })}>
                  {periods.filter((p) => p.status === "open").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </FormGroup>
              <Row>
                <Col md="6"><FormGroup><Label>Run date</Label><Input type="date" required value={form.run_date} onChange={(e) => setForm({ ...form, run_date: e.target.value })} /></FormGroup></Col>
                <Col md="12"><FormGroup><Label>Description</Label><Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormGroup></Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>Create</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default DepreciationPage;
